import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import adminService from '../services/adminService';

function AdminAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [filters, setFilters] = useState({
    userId: '',
    action: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [filters, pagination.page]);

  const loadInitialData = async () => {
    try {
      const [usersRes, actionsRes] = await Promise.all([
        adminService.getUsers(),
        adminService.getAuditLogActions()
      ]);
      setUsers(usersRes.data);
      setActions(actionsRes.data);
    } catch (err) {
      console.error('Failed to load initial data:', err);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: 25,
        ...filters
      };
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });
      
      const response = await adminService.getAuditLogs(params);
      setLogs(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError('Failed to load audit logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSelectLog = (logId) => {
    setSelectedLogs(prev => 
      prev.includes(logId) 
        ? prev.filter(id => id !== logId)
        : [...prev, logId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLogs.length === logs.length) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(logs.map(log => log._id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedLogs.length === 0) {
      alert('Please select logs to delete');
      return;
    }
    if (!window.confirm(`Delete ${selectedLogs.length} selected log(s)?`)) {
      return;
    }
    try {
      await adminService.deleteAuditLogs(selectedLogs);
      setSelectedLogs([]);
      loadLogs();
    } catch (err) {
      alert('Failed to delete logs');
    }
  };

  const handleExport = async (format) => {
    try {
      const params = { ...filters };
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });
      
      const response = await adminService.exportAuditLogs(format, params);
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      alert('Failed to export logs');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAction = (action) => {
    return action.replace(/_/g, ' ');
  };

  const getActionColor = (action) => {
    if (action.includes('FAILED')) return '#dc3545';
    if (action.includes('DELETE')) return '#fd7e14';
    if (action.includes('CREATE') || action.includes('REGISTER')) return '#28a745';
    if (action.includes('UPDATE') || action.includes('CHANGE')) return '#17a2b8';
    return '#6c757d';
  };

  return (
    <div className="app-container">
      <div className="container">
        <div className="page-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1>Audit Logs</h1>
              <p>Track user activities and system events</p>
            </div>
            <Link to="/admin" className="btn btn-secondary">Back to Admin</Link>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Filters */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header">Filters</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>User</label>
              <select
                name="userId"
                className="form-control"
                value={filters.userId}
                onChange={handleFilterChange}
              >
                <option value="">All Users</option>
                {users.map(user => (
                  <option key={user._id} value={user._id}>{user.name} ({user.email})</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Action</label>
              <select
                name="action"
                className="form-control"
                value={filters.action}
                onChange={handleFilterChange}
              >
                <option value="">All Actions</option>
                {actions.map(action => (
                  <option key={action} value={action}>{formatAction(action)}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                className="form-control"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>End Date</label>
              <input
                type="date"
                name="endDate"
                className="form-control"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <div className="card-header" style={{ marginBottom: 0 }}>
              Logs ({pagination.total} total)
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button 
                onClick={handleDeleteSelected} 
                className="btn btn-danger btn-sm"
                disabled={selectedLogs.length === 0}
              >
                Delete Selected ({selectedLogs.length})
              </button>
              <button onClick={() => handleExport('xlsx')} className="btn btn-primary btn-sm">
                Export XLSX
              </button>
              <button onClick={() => handleExport('csv')} className="btn btn-secondary btn-sm">
                Export CSV
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : (
            <>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={selectedLogs.length === logs.length && logs.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>Timestamp</th>
                      <th>User</th>
                      <th>Action</th>
                      <th>Status</th>
                      <th>IP Address</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                          No audit logs found
                        </td>
                      </tr>
                    ) : (
                      logs.map(log => (
                        <tr key={log._id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedLogs.includes(log._id)}
                              onChange={() => handleSelectLog(log._id)}
                            />
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>{formatDate(log.timestamp)}</td>
                          <td>
                            <div>{log.userName || 'N/A'}</div>
                            <small style={{ color: '#6c757d' }}>{log.userEmail || ''}</small>
                          </td>
                          <td>
                            <span style={{ 
                              color: getActionColor(log.action),
                              fontWeight: 600 
                            }}>
                              {formatAction(log.action)}
                            </span>
                          </td>
                          <td>
                            <span style={{ 
                              color: log.success ? '#28a745' : '#dc3545',
                              fontWeight: 600 
                            }}>
                              {log.success ? 'Success' : 'Failed'}
                            </span>
                          </td>
                          <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                            {log.ipAddress || 'N/A'}
                          </td>
                          <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {log.errorMessage || (log.details ? JSON.stringify(log.details).substring(0, 50) : '-')}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                  <span style={{ padding: '8px 16px' }}>
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminAuditLogs;
